import { Link } from 'react-router-dom';
import { CATEGORIES } from '../../data/catalog';
import { featuredExteriorRoutes, productPhotos } from '../../data/homeImages';
import SectionHeading from '../ui/SectionHeading';
import Reveal from '../ui/Reveal';
import { ArrowRight } from '../ui/Icons';

const category = CATEGORIES.exterior;

/** Four exterior products from the catalogue, shown on elevation photography. */
const FEATURED = featuredExteriorRoutes
  .map((route) => category.products.find((p) => p.route === route))
  .filter((p): p is NonNullable<typeof p> => Boolean(p))
  .concat(category.products.filter((p) => !featuredExteriorRoutes.includes(p.route)))
  .slice(0, 4);

const ExteriorPaints = () => (
  <section className="vp-section bg-brand-navy text-white relative overflow-hidden">
    {/* soft brand glow */}
    <div className="pointer-events-none absolute -top-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-brand-blue/40 blur-3xl" />
    <div className="pointer-events-none absolute -bottom-40 -left-32 w-[26rem] h-[26rem] rounded-full bg-brand-red/20 blur-3xl" />

    <div className="relative container">
      <SectionHeading
        eyebrow={category.eyebrow}
        title={category.tagline}
        lead="Sun, rain and dust are the real test of an exterior coat. Every Vertex exterior product is graded for UV stability, water resistance and resistance to algal growth."
        tone="light"
        className="mb-12"
        action={
          <Link to={category.route} className="vp-btn vp-btn-gold">
            View exterior range
            <ArrowRight size={16} />
          </Link>
        }
      />

      <ul className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {FEATURED.map((p, i) => {
          const photo = productPhotos[p.route];
          const title = p.title;
          return (
            <Reveal as="li" key={p.route} delay={Math.min(i, 4) as 0 | 1 | 2 | 3 | 4}>
              <Link
                to={p.route}
                className="group relative flex flex-col h-full rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-brand-gold/50 transition-all duration-500 ease-premium hover:-translate-y-1.5"
              >
                <div className="relative h-52 overflow-hidden bg-brand-navy">
                  {photo ? (
                    <img src={photo.image} alt={photo.alt} className="w-full h-full object-cover transition-transform duration-700 ease-premium group-hover:scale-110" loading="lazy" />
                  ) : (
                    p.image && <img src={p.image} alt={`${title} pack`} className="w-full h-full object-contain p-8" loading="lazy" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/25 to-transparent" />
                  {photo?.tag && <span className="absolute top-3 left-3 vp-tag !bg-brand-gold !text-brand-ink">{photo.tag}</span>}
                  {p.warranty && <span className="absolute bottom-3 left-3 vp-chip vp-chip-light text-[11px]">{p.warranty} warranty*</span>}
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="text-base font-semibold text-white group-hover:text-brand-gold transition-colors">{title}</h3>
                  {p.tagline && <p className="mt-0.5 text-xs uppercase tracking-wider text-white/50">{p.tagline}</p>}
                  {p.features.length > 0 && (
                    <ul className="mt-3 space-y-1.5 flex-1">
                      {p.features.slice(0, 2).map((f) => (
                        <li key={f} className="text-sm text-white/65 leading-snug">
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand-gold group-hover:gap-3 transition-all">
                    Product details
                    <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </Reveal>
          );
        })}
      </ul>
      <p className="mt-6 text-xs text-white/45">*Performance warranty as stated for each product. See the product page for details.</p>
    </div>
  </section>
);

export default ExteriorPaints;
